import app from 'flarum/forum/app';

/**
 * 全局上传活动状态（跨组件共享，横幅组件读取）。
 * 结构: { active: bool, tasks: [{name, loaded, total, pct}], done: [{name, bbcode}] }
 */
export const UploadActivity = {
  tasks: [], // 进行中的上传批次
  done: [], // 已完成文件
  _finished: false, // 本轮上传是否已全部结束（用于“已上传完成”横幅态）
  _activeUploader: null, // 当前活跃的 Uploader 实例（供横幅取消按钮调用 cancelAll）

  /** 注册/清空当前活动上传器 */
  setActiveUploader(u) {
    this._activeUploader = u || null;
  },

  /**
   * 注册一个上传批次，返回进度更新回调
   */
  begin(files) {
    const task = {
      name: files && files.length === 1 ? files[0].name : `${files.length} 个文件`,
      loaded: 0,
      total: 0,
      pct: 0,
    };
    this.tasks.push(task);
    this._finished = false;

    // 通知 UI 刷新
    m.redraw();

    return {
      update(loaded, total) {
        task.loaded = loaded;
        task.total = total;
        task.pct = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
      },
      /** 上传数据已全部发送完成（服务端可能仍在处理），进度直接置 100% */
      complete() {
        if (!task.total) task.total = task.loaded || 1;
        task.loaded = task.total;
        task.pct = 100;
        m.redraw();
      },
      finish() {
        const idx = UploadActivity.tasks.indexOf(task);
        if (idx !== -1) UploadActivity.tasks.splice(idx, 1);
        if (!UploadActivity.tasks.length) {
          UploadActivity._finished = true;
        }
        m.redraw();
      },
      /** 绑定取消回调（由 Uploader 的 config 钩子注册 xhr.abort） */
      onCancel(cb) {
        task._cancel = cb;
      },
      cancel() {
        if (task._cancel) task._cancel();
        const idx = UploadActivity.tasks.indexOf(task);
        if (idx !== -1) UploadActivity.tasks.splice(idx, 1);
        if (!UploadActivity.tasks.length) {
          UploadActivity._finished = true;
        }
        m.redraw();
      },
    };
  },

  /** 取消全部进行中的上传 */
  cancelAll() {
    const tasks = this.tasks.slice();
    tasks.forEach((t) => {
      if (t._cancel) t._cancel();
    });
    if (this.tasks.length) {
      this.tasks = [];
      this._finished = true;
    }
    m.redraw();
  },

  /** 本轮上传已全部结束（tasks 清空后置 true），横幅据此切换“已上传完成”态 */
  isFinished() {
    return this._finished && this.done.length > 0;
  },

  /** 用户点了叉叉，关闭完成态横幅（同一轮内不再自动出现，直到下次新上传） */
  dismissFinished() {
    this._finished = false;
    m.redraw();
  },

  /** 记录一个上传成功的文件 */
  addDone(fileObj, bbcode) {
    this.done.push({
      name: fileObj.baseName ? fileObj.baseName() : fileObj.url(),
      bbcode: bbcode || '',
      url: fileObj.url ? fileObj.url() : '',
      type: fileObj.type ? fileObj.type() : '',
    });
  },

  isActive() {
    return this.tasks.length > 0;
  },

  /** 总进度百分比 (所有活跃批次合并) */
  overallPct() {
    if (!this.tasks.length) return 0;
    let loaded = 0;
    let total = 0;
    this.tasks.forEach((t) => {
      loaded += t.loaded;
      total += t.total;
    });
    return total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
  },
};

export default class Uploader {
  constructor() {
    this.callbacks = {
      success: [],
      failure: [],
      uploading: [],
      uploaded: [],
    };
    this.uploading = false;
    UploadActivity.setActiveUploader(this);

    /**
     * 允许"先发布后上传完成"模式。当 composer 已关闭（帖子已发布）而上传
     * 仍在进行时，上传完成的 bbcode 不再插入编辑器，而是收集到这里，
     * 由 addUploadButton 弹窗询问是否插入刚发布的帖子。
     */
    this.backgroundMode = false;
    this.backgroundBbcode = [];
  }

  setState(fileState) {
    this.fileState = fileState;
  }

  on(type, callback) {
    this.callbacks[type].push(callback);
  }

  dispatch(type, response) {
    this.callbacks[type].forEach((callback) => callback(response));
  }

  upload(files, addBBcode = true) {
    this.uploading = true;
    this.retried = false;
    this.cancelled = false;
    this.dispatch('uploading', files);

    // 注册全局活动（横幅 UI 数据源）
    const act = UploadActivity.begin(files);

    m.redraw(); // Forcing a redraw so that the button also updates if uploadFiles() is called from DragAndDrop or PasteClipboard

    const body = new FormData();

    for (let i = 0; i < files.length; i++) {
      body.append('files[]', files[i]);
    }

    let xhrRef = null;

    // send a POST request to the api
    return app
      .request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/fof/upload',
        // prevent JSON.stringify'ing the form data in the XHR call
        serialize: (raw) => raw,
        body,
        // 上传进度钩子：Flarum/mithril 不透传 onuploadprogress，需用 config 拿原生 xhr 挂 upload.onprogress
        config: (xhr) => {
          xhrRef = xhr;
          act.onCancel(() => {
            try {
              xhr.abort();
            } catch (e) {
              // ignore
            }
          });
          if (xhr && xhr.upload) {
            xhr.upload.onprogress = (e) => {
              if (e && e.lengthComputable) {
                act.update(e.loaded, e.total);
                m.redraw();
              }
            };
            // 数据发送完毕即视为上传完成（服务端响应可能因大文件处理而延迟）
            xhr.upload.onload = () => {
              act.complete();
            };
          }
          return xhr;
        },
      })
      .then((result) => {
        act.finish();
        return this.uploaded(result, addBBcode);
      })
      .catch((error) => {
        act.finish();
        this.uploading = false;
        m.redraw();

        // 用户主动取消 → 静默结束，不进重试/报错流程
        if (this.cancelled) {
          this.cancelled = false;
          this.dispatch('uploaded'); // 让收集链结束（pending 为空则无操作）
          return;
        }

        // 失败自动重试一次（仅网络/服务端 5xx 等可重试错误）
        if (!this.retried && error && (!error.response || (error.response.status >= 500 && error.response.status < 600))) {
          this.retried = true;
          return this.upload(files, addBBcode);
        }

        const e = error.response?.errors?.[0];

        if (!e || !e.code.includes('fof-upload')) {
          throw error;
        }

        app.alerts.clear();
        app.alerts.show(
          {
            type: 'error',
          },
          e.detail
        );
      });
  }

  /**
   * 取消所有正在进行中的上传（横幅“取消上传”按钮）。
   * abort() 会让 XHR 走 catch 分支，那里检测 this.cancelled 后静默收尾。
   */
  cancelAll() {
    this.cancelled = true;
    UploadActivity.cancelAll();
  }

  uploaded(result, addBBcode = false) {
    this.uploading = false;
    this.retried = false;

    result.data.forEach((file) => {
      const fileObj = app.store.pushObject(file);

      // Add file to media manager
      this.fileState?.addToList(fileObj);

      // 记录到全局"已完成"列表（横幅展示）
      UploadActivity.addDone(fileObj);

      // Dispatch
      this.dispatch('success', {
        file: fileObj,
        addBBcode,
      });
    });

    this.dispatch('uploaded');
    m.redraw();
  }
}
